import { useMemo } from "react"
import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { ExternalLink } from "../../../../../../../shared/components/links/ExternalLink"
import { LoadingSpinner } from "../../../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { Workgroup } from "../../../workgroups/workgroupsTypes"
import { CompanyUserFormValues } from "../../companyUserFormTypes"

type WorkgroupsTabProps = Readonly<{
    workgroups: ReadonlyArray<Workgroup>
    isLoading: boolean
}>

const useStyles = makeStyles()((theme) => ({
    loading: {
        display: "flex",
        justifyContent: "center",
        padding: "2.5rem 0",
    },
    card: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".75rem",
        ":hover": {
            backgroundColor: theme.palette.tailwind.gray[50],
        },
    },
    row: {
        alignItems: "flex-start",
        width: "100%",
        margin: 0,
        padding: "1rem",
    },
    checkbox: {
        "&.MuiCheckbox-root": {
            paddingTop: 0,
        },
    },
    label: {
        display: "flex",
        flexDirection: "column",
        gap: ".25rem",
    },
    name: {
        fontWeight: 700,
    },
    description: {
        marginTop: ".25rem",
        color: theme.palette.grey[700],
    },
    workgroupHeader: {
        display: "flex",
        flexDirection: "column",
    },
    workgroupMail: {
        color: theme.palette.grey[600],
        fontSize: "0.75rem",
    },
}))

type WorkgroupItemProps = Readonly<{
    workgroup: Workgroup
    checked: boolean
    onToggle: () => void
}>

function WorkgroupItem({ workgroup, checked, onToggle }: WorkgroupItemProps) {
    const { classes } = useStyles()

    return (
        <Box className={classes.card}>
            <FormControlLabel
                className={classes.row}
                control={<Checkbox className={classes.checkbox} checked={checked} onChange={onToggle} />}
                label={
                    <Box className={classes.label}>
                        <Box className={classes.workgroupHeader}>
                            <Typography variant="subtitle1" className={classes.name}>
                                {workgroup.name}
                            </Typography>
                            {workgroup.calendarEmailAddress && (
                                <Typography variant="body2" className={classes.workgroupMail}>
                                    <LocalizedStrict id="company-user-workgroups-calendar-email-label">
                                        <Box component="span">Calendar email:</Box>
                                    </LocalizedStrict>{" "}
                                    <ExternalLink
                                        href={`mailto:${workgroup.calendarEmailAddress}`}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        {workgroup.calendarEmailAddress}
                                    </ExternalLink>
                                </Typography>
                            )}
                        </Box>
                        {workgroup.description && (
                            <Typography variant="body2" color="text.primary" className={classes.description}>
                                {workgroup.description}
                            </Typography>
                        )}
                    </Box>
                }
            />
        </Box>
    )
}

export function WorkgroupsTab({ workgroups, isLoading }: WorkgroupsTabProps) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CompanyUserFormValues>()

    const selectedIds = useMemo(() => new Set(values.workgroups.map((workgroup) => workgroup.id)), [values.workgroups])

    const toggle = (workgroup: Workgroup) => {
        if (selectedIds.has(workgroup.id)) {
            setFieldValue(
                "workgroups",
                values.workgroups.filter((selected) => selected.id !== workgroup.id),
            )
        } else {
            setFieldValue("workgroups", [...values.workgroups, { id: workgroup.id, name: workgroup.name }])
        }
    }

    return (
        <Stack direction="column" spacing="1rem">
            <LocalizedStrict id="company-user-workgroups-tab-description">
                <Typography variant="body2" color="text.secondary">
                    Select one or more workgroups for the user. In a workgroup, members can work together — for example,
                    see each other's bookings and meeting service orders through a shared calendar.
                </Typography>
            </LocalizedStrict>

            {isLoading ? (
                <Box className={classes.loading}>
                    <LoadingSpinner />
                </Box>
            ) : workgroups.length === 0 ? (
                <LocalizedStrict id="company-user-workgroups-empty">
                    <Typography variant="body2" color="text.secondary">
                        No workgroups available.
                    </Typography>
                </LocalizedStrict>
            ) : (
                <Stack direction="column" spacing=".75rem">
                    {workgroups.map((workgroup) => (
                        <WorkgroupItem
                            key={workgroup.id}
                            workgroup={workgroup}
                            checked={selectedIds.has(workgroup.id)}
                            onToggle={() => toggle(workgroup)}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    )
}
